'use client';

import { useEffect } from 'react';

type HotkeyHandler = () => void;

export interface HotkeyMap {
  [key: string]: HotkeyHandler;
}

/**
 * 注册全局键盘快捷键。
 * - 输入框/文本域中的按键会被忽略（避免打断用户输入）
 * - 组合键用 'mod+k'（mod = Ctrl on Win/Linux, Cmd on Mac）
 * - 单键用 'c'、'/'、'?' 等
 */
export function useHotkeys(map: HotkeyMap) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // 在输入控件中时，除了 esc 和 mod+* 之外都放行原生行为
      const target = e.target as HTMLElement | null;
      const inEditable =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      for (const [combo, fn] of Object.entries(map)) {
        if (matchCombo(combo, e)) {
          // 输入控件里只有 mod+ 组合键还生效
          if (inEditable && !combo.startsWith('mod+')) continue;
          e.preventDefault();
          fn();
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [map]);
}

function matchCombo(combo: string, e: KeyboardEvent): boolean {
  const parts = combo.toLowerCase().split('+');
  const key = parts[parts.length - 1];
  const needMod = parts.includes('mod');
  const needShift = parts.includes('shift');
  const needAlt = parts.includes('alt');

  const isMod = e.metaKey || e.ctrlKey;
  if (needMod !== isMod) return false;
  if (needShift !== e.shiftKey) return false;
  if (needAlt !== e.altKey) return false;

  // `?` 在大多数键盘需要 shift，但用户直觉是 `?` 一个键
  if (key === '?') return e.key === '?' || (e.shiftKey && e.key === '/');
  return e.key.toLowerCase() === key;
}
